// eslint-disable-next-line import/no-extraneous-dependencies
import { User } from '../../models/user.types';
import { Cart } from '../../models/cart.types';

export interface GTMAddToCartItem {
  itemName: string;
  itemId: string;
  price: number;
  quantity: number;
}

export interface GTMAddToCartPayload {
  price: number;
  currency: string;
  ecomPagetype: 'home' | 'product' | 'basket';
  item: GTMAddToCartItem;
}

export const gtmTrackAddToCart = (payload: GTMAddToCartPayload): void => {
  // eslint-disable-next-line no-restricted-globals
  const eventId = self.crypto.randomUUID();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    event_id: eventId,
    ecommerce: {
      value: payload.price,
      currency: payload.currency,
      ecom_pagetype: payload.ecomPagetype,
      items: [
        {
          item_name: payload.item.itemName,
          item_id: payload.item.itemId,
          price: payload.item.price,
          quantity: payload.item.quantity,
        },
      ],
    },
  });
};

export const gtmTrackOrderSuccess = (
  user: User,
  cart: Cart,
  eventCallback?: () => void,
  eventTimeout?: number,
): void => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'ordersuccess',
    transactionId: user.subscriptionId,
    transactionAffiliation: 'The Purple Carrot',
    transactionTotal: cart.priceSummary.total,
    transactionTax: cart.priceSummary.tax,
    transactionShipping: cart.priceSummary.shipping,
    currency: 'USD',
    transactionProducts: [{
      sku: user.plan,
      name: user.plan,
      category: 'Meals',
      price: cart.priceSummary.total,
      quantity: 1,
    }],
    userData: {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: `+${user.phoneNumber}`,
      address: {
        postalCode: user.zipCode,
      },
    },
    eventCallback,
    eventTimeout,
  });
};

export const gtmTrackAccountCreated = (user: User): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'account_created',
    transactionId: user.id,
    transactionAffiliation: 'The Purple Carrot',
    userData: {
      email: user.email,
    },
  });
};

export const gtmTrackBeginCheckout = (cart: Cart): void => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });
  const mappedItems = (cart.cartItems || []).map((cartItem: any) => ({
    item_name: cartItem.title,
    item_id: cartItem.skuId ? cartItem.skuId.toString() : '',
    price: parseFloat(cartItem.skuPrice?.toString()
      .replace(/[^0-9.]/g, '') || '0'),
    quantity: cartItem.quantity,
  }));

  window.dataLayer.push({
    event: 'begin_checkout',
    event_id: crypto.randomUUID(),
    ecommerce: {
      value: parseFloat(cart.priceSummary?.total?.replace(/[^0-9.]/g, '') || '0'),
      currency: 'USD',
      ecom_pagetype: 'basket',
      items: mappedItems,
    },
  });
};

export const gtmTrackViewItem = (product: any, sku: any): void => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });
  const basePrice = parseFloat(sku.price?.toString()
    .replace(/[^0-9.]/g, '') || '0');
  const salePrice = sku.sale_price
    ? parseFloat(sku.sale_price.toString()
      .replace(/[^0-9.]/g, ''))
    : null;
  // eslint-disable-next-line no-restricted-globals
  const finalValue = salePrice !== null && !isNaN(salePrice) ? salePrice : basePrice;

  const payload: any = {
    event: 'view_item',
    // eslint-disable-next-line no-restricted-globals
    event_id: self.crypto.randomUUID(),
    ecommerce: {
      value: finalValue,
      currency: 'USD',
      ecom_pagetype: 'product',
      items: [
        {
          item_name: product.title,
          item_id: sku.id ? sku.id.toString() : '',
          price: basePrice,
          quantity: 1,
        },
      ],
    },
  };
  window.dataLayer.push(payload);
};

export const gtmTrackViewCart = (cart: Cart): void => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });
  const mappedItems = (cart.cartItems || []).map((cartItem: any) => ({
    item_name: cartItem.title,
    item_id: cartItem.skuId ? cartItem.skuId.toString() : '',
    price: parseFloat(cartItem.skuPrice?.toString()
      .replace(/[^0-9.]/g, '') || '0'),
    quantity: cartItem.quantity,
  }));
  const payload: any = {
    event: 'view_cart',
    // eslint-disable-next-line no-restricted-globals
    event_id: self.crypto.randomUUID(),
    ecommerce: {
      value: parseFloat(cart.priceSummary?.total?.toString()
        .replace(/[^0-9.]/g, '') || '0'),
      currency: 'USD',
      ecom_pagetype: 'basket',
      items: mappedItems,
    },
  };
  window.dataLayer.push(payload);
};
